import React, { useEffect, FC } from 'react';
import updeep from 'updeep';
import { CandidateProfileActionsModal, ACTIONS_MODAL_CLASS_NAME } from '@src/containers/CandidateProfileActionsModal';
import { getCandidateProfileId } from '@src/redux/selectors/candidateProfile';
import { setMessageState, updateApplications } from '@src/redux/actions/applications';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { closeCandidateProfile } from '@src/redux/actions/candidateProfile';
import { setModalContent, closeModal } from '@src/redux/actions/modal';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import {
  GetOpeningCandidateProfileApplicationQuery,
  InboxMessageAddedDocument,
  InboxMessageAddedSubscription,
  InboxMessageAddedSubscriptionVariables,
  MessageThreadStateEnum,
  PaginationSortOrderEnum,
  useAcceptApplicationMutation,
  useGetOpeningCandidateProfileApplicationLazyQuery,
  useMarkInboxMessageThreadAsReadMutation,
  useMarkInboxMessageThreadAsUnreadMutation,
  useRejectApplicationMutation,
  useSendInboxMessageMutation,
  useSessionQuery,
  useUpdateInboxMessageThreadMutation,
} from '@src/graphql/generated';
import { useAppDispatch, useAppSelector } from '@src/redux';
import { CandidateProfileWithAnimation } from '@src/containers/CandidateProfileWithAnimation';
import { useChatMessage } from '@src/customHooks/useChatMessage';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { usePrevious } from '@src/customHooks/usePrevious';
import { trackEvent } from '@src/metrics';

const RECRUITER_STATE_TO_SUCCESS_MESSAGE: Record<MessageThreadStateEnum, string> = {
  [MessageThreadStateEnum.OPEN]: 'Chat reopened',
  [MessageThreadStateEnum.CLOSED]: 'Chat closed',
  [MessageThreadStateEnum.ARCHIVED]: 'Chat archived',
};

export const OpeningCandidateProfile: FC = () => {
  const dispatch = useAppDispatch();

  const applicationId: string | undefined = useAppSelector(getCandidateProfileId);

  const prevApplicationId = usePrevious(applicationId);

  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;

  const setUpChat = (application: GetOpeningCandidateProfileApplicationQuery['application']) => {
    dispatch(
      setMessageState({
        id: application.id,
        lastMessageDate: application.messageThread?.latestMessageSentAt,
      }),
    );
  };

  const [
    fetchApplication,
    {
      data: applicationData,
      loading: isApplicationLoading,
      refetch: refetchApplication,
      subscribeToMore: subscribeToMoreMessageThread,
    },
  ] = useGetOpeningCandidateProfileApplicationLazyQuery({
    onCompleted: data => {
      setUpChat(data.application);
    },
  });

  const application = applicationData?.application;
  const openingId = application?.opening?.id;
  const messageThread = application?.messageThread;
  const threadId = application?.messageThread?.id;
  const recruiterChatState = application?.messageThread?.recruiterState;
  const userMatchingProfileId = application?.userMatchingProfile?.id;

  const { chatMessage, setChatMessage, clearChatMessage } = useChatMessage({
    chatId: application?.messageThread?.id,
  });

  const handleApplicationRefetch = async () => {
    if (!applicationId?.length) {
      return undefined;
    }

    return refetchApplication({
      id: applicationId,
      messagesPaginationOptions: {
        sortOrder: PaginationSortOrderEnum.ASC,
      },
      recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
    });
  };

  const [sendInboxMessage, { loading: isSendingInboxMessage }] = useSendInboxMessageMutation({
    onCompleted: async () => {
      clearChatMessage();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error sending message'));
    },
  });

  const [acceptApplication, { loading: isAcceptingApplication }] = useAcceptApplicationMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Application accepted'));

      await handleApplicationRefetch();

      const updatedApplication = updeep.updateIn(
        'application',
        {
          recruiterState: { state: 'accepted' },
          state: 'accepted',
        },
        application,
      );

      dispatch(updateApplications([updatedApplication]));

      clearChatMessage();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error accepting application'));
    },
  });

  const [rejectApplication, { loading: isRejectingApplication }] = useRejectApplicationMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Application rejected'));

      await handleApplicationRefetch();

      clearChatMessage();

      const updatedApplication = updeep.updateIn(
        'application',
        {
          recruiterState: { state: 'rejected' },
          state: 'rejected',
        },
        application,
      );

      dispatch(updateApplications([updatedApplication]));

      dispatch(closeCandidateProfile());
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error rejecting application'));
    },
  });

  const [updateInboxMessageThread, { loading: isUpdatingInboxMessageThread }] = useUpdateInboxMessageThreadMutation({
    onCompleted: async updateInboxMessageThreadData => {
      const updatedRecruiterState = updateInboxMessageThreadData.updateMessageThread.recruiterState;

      dispatch(setSuccessFlashMessage(RECRUITER_STATE_TO_SUCCESS_MESSAGE[updatedRecruiterState]));

      if (updatedRecruiterState === MessageThreadStateEnum.ARCHIVED) {
        const updatedApplication = updeep.updateIn('application.threadStateRecruiter', 'archived', application);

        dispatch(updateApplications([updatedApplication]));

        trackEvent({
          context: MetricContext.CHAT,
          interaction: MetricAction.ARCHIVE_CHAT,
          payload: {
            'Application Id': applicationId,
            'Event Source': MetricEventSource.CANDIDATE_PROFILE_ARCHIVE_ACTION,
            'Opening Id': openingId,
            'Previous Context': MetricContext.OPENING_VIEW,
            'Thread Id': threadId,
          },
        });

        return;
      }

      if (updatedRecruiterState === MessageThreadStateEnum.CLOSED) {
        const updatedApplication = updeep.updateIn('application.threadStateRecruiter', 'closed', application);

        dispatch(updateApplications([updatedApplication]));

        return;
      }

      if (MessageThreadStateEnum.OPEN === updatedRecruiterState) {
        const updatedApplication = updeep.updateIn(
          'application',
          {
            recruiterState: { state: 'accepted' },
            state: 'accepted',
            threadStateRecruiter: 'open',
          },
          application,
        );

        dispatch(updateApplications([updatedApplication]));

        await handleApplicationRefetch();
      }
    },
    onError: () => {
      dispatch(setErrorFlashMessage());
    },
  });

  const [markMessageThreadAsUnread] = useMarkInboxMessageThreadAsUnreadMutation({
    onCompleted: async data => {
      dispatch(closeCandidateProfile());

      dispatch(
        setMessageState({
          id: applicationId,
          isUnread: true,
          lastMessageDate: data.markMessageThreadAsUnread.latestMessageSentAt,
        }),
      );

      dispatch(setSuccessFlashMessage('Chat marked as unread'));
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error marking chat as unread'));
    },
  });

  const [markMessageThreadAsRead] = useMarkInboxMessageThreadAsReadMutation({
    onError: () => {
      dispatch(setErrorFlashMessage('Error marking chat as read'));
    },
  });

  const markRead = async (threadId: string) => {
    await markMessageThreadAsRead({
      variables: {
        messageThreadId: threadId,
      },
    });
  };

  const isSending =
    isSendingInboxMessage ||
    isAcceptingApplication ||
    isRejectingApplication ||
    isApplicationLoading ||
    isUpdatingInboxMessageThread;

  const onSend = async (messageText: string = chatMessage): Promise<void> => {
    if (!threadId || !messageText.length) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await sendInboxMessage({
      variables: {
        messageInput: {
          message: messageText,
        },
        messageThreadId: threadId,
      },
    });
  };

  const onAccept = async () => {
    if (!userMatchingProfileId || !openingId) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await acceptApplication({
      variables: {
        message: chatMessage,
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });
  };

  const onReject = async () => {
    if (!userMatchingProfileId || !openingId) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await rejectApplication({
      variables: {
        message: chatMessage,
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });
  };

  const onChatReopen = async () => {
    if (!applicationData?.application?.messageThread?.id) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await updateInboxMessageThread({
      variables: {
        messageThreadId: applicationData.application.messageThread.id,
        updateInput: {
          state: MessageThreadStateEnum.OPEN,
        },
      },
    });
  };

  const onChatClose = async () => {
    if (!applicationData?.application?.messageThread?.id) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await updateInboxMessageThread({
      variables: {
        messageThreadId: applicationData.application.messageThread.id,
        updateInput: {
          state: MessageThreadStateEnum.CLOSED,
        },
      },
    });
  };

  const archiveChat = async () => {
    if (!applicationData?.application?.messageThread?.id) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await updateInboxMessageThread({
      variables: {
        messageThreadId: applicationData.application.messageThread.id,
        updateInput: {
          state: MessageThreadStateEnum.ARCHIVED,
        },
      },
    });
  };

  const markAsUnread = async () => {
    if (!threadId) {
      dispatch(setErrorFlashMessage());

      return;
    }

    await markMessageThreadAsUnread({
      variables: {
        messageThreadId: threadId,
      },
    });
  };

  const openActionsModal = async () => {
    dispatch(
      setModalContent({
        className: ACTIONS_MODAL_CLASS_NAME,
        content: (
          <CandidateProfileActionsModal
            applicationId={applicationId}
            closeModal={() => dispatch(closeModal())}
            defaultInputMessage={chatMessage}
            onChatClosed={() => onChatClose()}
            openingId={openingId}
            sendMessage={onSend}
            threadId={threadId}
          />
        ),
      }),
    );

    trackEvent({
      context: MetricContext.END_CHAT,
      interaction: MetricAction.OPEN_END_CHAT_VIEW,
      payload: {
        'Application Id': applicationId,
        'Event Source': MetricEventSource.CANDIDATE_PROFILE_CLOSE_CHAT_ACTION,
        'Opening Id': openingId,
        'Previous Context': MetricContext.OPENING_VIEW,
        'Thread Id': threadId,
        'Type': 'Close',
      },
    });
  };

  useEffect(() => {
    if (!applicationId?.length) {
      return;
    }

    const initiateMessages = async () => {
      if (!applicationData) {
        await fetchApplication({
          variables: {
            id: applicationId,
            messagesPaginationOptions: {
              sortOrder: PaginationSortOrderEnum.ASC,
            },
            recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
          },
        });
      } else {
        await handleApplicationRefetch();
      }
    };

    initiateMessages();
  }, [applicationId]);

  useEffect(() => {
    if (!threadId?.length) {
      return;
    }

    const unsubscribeToMoreMessageThread = subscribeToMoreMessageThread<
      InboxMessageAddedSubscription,
      InboxMessageAddedSubscriptionVariables
    >({
      document: InboxMessageAddedDocument,
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data || !prev?.application?.messageThread) {
          return prev;
        }

        const newMessage = subscriptionData.data.messageAdded;

        const doesMessageExist = prev.application.messageThread.messages.result.some(message => {
          return message?.id === newMessage.id;
        });

        if (doesMessageExist || prev.application.messageThread.id !== threadId) {
          return prev;
        }

        return {
          ...prev,
          application: {
            ...prev.application,
            messageThread: {
              ...prev.application.messageThread,
              messages: {
                ...prev.application.messageThread?.messages,
                result: [...(prev.application.messageThread?.messages.result || []), newMessage],
              },
            },
          },
        };
      },
      variables: {
        messageThreadId: threadId,
      },
    });

    return () => {
      unsubscribeToMoreMessageThread?.();
    };
  }, [threadId]);

  useEffect(() => {
    if (applicationId && prevApplicationId !== applicationId && threadId && messageThread?.isUnread) {
      markRead(threadId);
    }
  }, [threadId, messageThread?.isUnread, applicationId, prevApplicationId]);

  const actions = [];

  if (applicationData?.application.messageThread?.messages.result?.[0]?.readByRecruiterAt) {
    actions.push({
      action: markAsUnread,
      label: 'Mark as unread',
    });
  }

  if (recruiterChatState === MessageThreadStateEnum.OPEN) {
    actions.push({
      action: () => openActionsModal(),
      label: 'Close',
    });
  }

  actions.push({
    action: archiveChat,
    destructive: true,
    label: 'Delete',
  });

  return (
    <CandidateProfileWithAnimation
      actions={actions}
      application={application}
      isApplicationLoading={isApplicationLoading}
      isOpen={!!applicationId}
      isSending={isSending}
      message={chatMessage}
      metricsContext={MetricContext.OPENING_VIEW}
      onAccept={onAccept}
      onChatClose={onChatClose}
      onChatReopen={onChatReopen}
      onClose={() => dispatch(closeCandidateProfile())}
      onInputChange={message => setChatMessage(message)}
      onReject={onReject}
      onSend={onSend}
    />
  );
};
